import React, { useState, useRef, useEffect } from "react";
import { useLocation } from 'react-router-dom';
import ScoreDisplay from "./ScoreDisplay";
import { trackEvent } from "./hooks/useGoogleAnalytics";
import { useLanguage } from './context/LanguageContext';
import { useDashboardLanguage } from './context/DashboardLanguageContext';

const TeamSection = ({ 
  teamName, 
  score, 
  onScoreChange, 
  onNameChange, 
  hasTimeout, 
  onTimeoutClick, 
  onStartTimeout,
  onCancelTimeout,
  onDecreaseTimeout,
  isTimeoutRequested,
  isLiveView = false,
  timeoutCount = 0,
  timeoutRunning = false,
  team = 'home',
}) => {
  const { t } = useLanguage();
  const { t: dt } = useDashboardLanguage();
  const location = useLocation();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(teamName);
  const originalNameRef = useRef(teamName);
  const placeholderName = team === 'home' ? dt('teams.home') : dt('teams.away');
  
  // Track if we're on the control page
  const isControlPage = location.pathname === '/control';
  
  // Update originalNameRef when teamName prop changes (e.g., on reset)
  useEffect(() => {
    originalNameRef.current = teamName;
  }, [teamName]);

  const handleNameChange = (newName) => {
    // Check if this is a complete name change (not just editing existing name)
    const isCompleteNameChange = 
      isControlPage && 
      originalNameRef.current !== newName && 
      newName !== placeholderName &&
      newName.length > 0;
    
    if (isCompleteNameChange) {
      // Track Google Analytics event for team name change
      trackEvent('control_namechange', {
        team_name: newName,
        previous_name: originalNameRef.current,
        team_side: placeholderName.toLowerCase().replace(' team', '') // 'home' or 'away'
      });
    }
    
    // Update the original name reference
    originalNameRef.current = newName;
    
    // Call the parent's onNameChange
    onNameChange(newName);
    setEditValue(newName);
    setIsEditing(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedValue = editValue.trim();
    const newName = trimmedValue || placeholderName;
    handleNameChange(newName);
  };

  const handleBlur = () => {
    const trimmedValue = editValue.trim();
    const newName = trimmedValue || placeholderName;
    handleNameChange(newName);
  };

  return (
    <div className="flex flex-col items-center justify-start h-full w-full px-[2vh] pt-[6vh] gap-[3vh]">
      {isLiveView ? (
        <div className="text-[min(7vh,5vw)] leading-[1.1] font-bold text-center w-full max-w-[70vh] line-clamp-2 break-words h-[calc(min(14vh,10vw)*1.1)]">
          {teamName || placeholderName}
        </div>
      ) : isEditing ? (
        <form onSubmit={handleSubmit} className="w-full max-w-[70vh]">
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleBlur}
            className="text-[min(7vh,5vw)] leading-[1.1] font-bold text-center bg-transparent border-b-2 border-gray-300 focus:outline-none focus:border-amber-500 w-full line-clamp-2 break-words h-[calc(min(14vh,10vw)*1.1)]"
            autoFocus
          />
        </form>
      ) : (
        <div 
          onClick={() => setIsEditing(true)}
          className="text-[min(7vh,5vw)] leading-[1.1] font-bold cursor-pointer hover:text-amber-500 text-center w-full max-w-[70vh] line-clamp-2 break-words h-[calc(min(14vh,10vw)*1.1)]"
        >
          {teamName || placeholderName}
        </div>
      )}
      <div className="flex items-center justify-center gap-[2vh]">
        {!isLiveView && (
          <button
            onClick={() => onScoreChange(-1)}
            className="w-[8vh] h-[8vh] bg-amber-500 text-black text-[min(4vh,2.5vw)] font-bold rounded-lg hover:bg-amber-600"
          >
            -
          </button>
        )}
        <ScoreDisplay score={score} isLiveView={isLiveView} />
        {!isLiveView && (
          <button
            onClick={() => onScoreChange(1)}
            className="w-[8vh] h-[8vh] bg-amber-500 text-black text-[min(4vh,2.5vw)] font-bold rounded-lg hover:bg-amber-600"
          >
            +
          </button>
        )}
      </div>
      <div className="mt-[1vh] min-h-[6vh] flex flex-col items-center justify-center gap-2">
        {isTimeoutRequested ? (
          <div className="flex justify-between items-center w-full gap-4">
            {timeoutRunning && (
            <div className="text-red-500 text-[min(6vh,2.5vw)] font-bold">
              {t('timeout.timeout')}
          </div>
            )}
            {!timeoutRunning && (
            <button
              onClick={onStartTimeout}
              className="px-4 py-2 bg-red-500 text-white text-[min(2vh,1.25vw)] font-bold rounded-lg hover:bg-red-600"
            >
              {t('timeout.startTimeout')}
            </button>
            )}
            {!timeoutRunning && (
              <button
                onClick={onCancelTimeout}
                className="px-4 py-2 bg-gray-500 text-white text-[min(2vh,1.25vw)] font-bold rounded-lg hover:bg-gray-600"
              >
                {t('timeout.cancelTimeout')}
              </button>
            )}
          </div>
        ) : hasTimeout ? (
          <div className="flex justify-between items-center w-full gap-4">
            <div className="text-red-500 text-[min(6vh,2.5vw)] font-bold">
              {t('timeout.timeout')}
            </div>
              {!isLiveView && (
                <button
                  onClick={onCancelTimeout}
                  className="px-4 py-2 bg-gray-500 text-white text-[min(2vh,1.25vw)] font-bold rounded-lg hover:bg-gray-600"
                >
                  {t('timeout.cancelTimeout')}
                </button>
              )}          </div>
        ) : !isLiveView && (
          <div className="flex items-center gap-2">
            {timeoutCount < 2 && (
              <button
                onClick={onTimeoutClick}
                className="px-4 py-2 bg-amber-500 text-black text-[min(2vh,1.25vw)] font-bold rounded-lg hover:bg-amber-600"
              >
                {t('timeout.requestTimeout')}
              </button>
            )}
            <div className="flex gap-1">
              {[...Array(timeoutCount)].map((_, i) => (
                <button
                  key={i}
                  onClick={onDecreaseTimeout}
                  className="w-[min(2vh,1.25vw)] h-[min(2vh,1.25vw)] bg-red-500 rounded-full hover:bg-red-600 cursor-pointer"
                  title={t('timeout.removeTimeout')}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamSection;